import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { employeeDayOffPatternsAPI, employeesAPI, QUERY_KEYS } from "@/api";
import { Badge, Button, Card, CardContent, Input, Modal } from "@/components/ui";
import { LoadingSpinner, Pagination } from "@/components/common";
import { Calendar, Edit2, Info, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { paginateClientData } from "@/utils/pagination";

const DAY_NAMES = ["Yak", "Du", "Se", "Cho", "Pay", "Ju", "Sha"];

const monthToDate = (monthValue: string): string | null => {
  if (!monthValue) return null;
  return `${monthValue}-01`;
};

const dateToMonth = (dateValue?: string | null): string => {
  if (!dateValue) return "";
  return String(dateValue).slice(0, 7);
};

const weekdaysLabel = (days: number[] = []) => {
  if (!Array.isArray(days) || days.length === 0) return "-";
  return [...days]
    .sort((a, b) => a - b)
    .map((d) => DAY_NAMES[d] ?? String(d))
    .join(", ");
};

export default function EmployeeDayOffsPage() {
  const queryClient = useQueryClient();
  const perPage = 10;

  const [page, setPage] = useState(1);
  const [showFormModal, setShowFormModal] = useState(false);
  const [filters, setFilters] = useState({ employee_id: "", is_active: "" });

  const [editing, setEditing] = useState<any>(null);
  const [employeeId, setEmployeeId] = useState("");
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [reason, setReason] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [effectiveMonth, setEffectiveMonth] = useState("");

  const { data: employeesData } = useQuery({
    queryKey: QUERY_KEYS.employees(),
    queryFn: () => employeesAPI.list({ per_page: 1000 }),
  });

  const { data: patternsData, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.employeeDayOffPatterns(), filters],
    queryFn: () => employeeDayOffPatternsAPI.list(filters),
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      employee_id: number;
      weekdays: number[];
      reason: string;
      is_paid?: boolean;
      effective_from?: string | null;
      is_active?: boolean;
    }) => employeeDayOffPatternsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employeeDayOffPatterns() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
      toast.success("Dam kuni jadvali saqlandi");
      resetForm();
      setShowFormModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: {
      weekdays?: number[];
      reason?: string;
      is_paid?: boolean;
      effective_from?: string | null;
      is_active?: boolean;
    } }) => employeeDayOffPatternsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employeeDayOffPatterns() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
      toast.success("Dam kuni jadvali yangilandi");
      resetForm();
      setShowFormModal(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => employeeDayOffPatternsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employeeDayOffPatterns() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
      toast.success("Dam kuni jadvali o'chirildi");
    },
  });

  const employees = Array.isArray(employeesData?.data)
    ? employeesData.data
    : employeesData?.data?.data || [];

  const allPatterns = Array.isArray(patternsData?.data)
    ? patternsData.data
    : patternsData?.data?.data || [];

  const { items: patterns, meta } = paginateClientData<any>(allPatterns, page, perPage);

  const toggleWeekday = (day: number) => {
    setWeekdays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const resetForm = () => {
    setEditing(null);
    setEmployeeId("");
    setWeekdays([]);
    setReason("");
    setIsPaid(false);
    setIsActive(true);
    setEffectiveMonth("");
  };

  const openCreateModal = () => {
    resetForm();
    setShowFormModal(true);
  };

  const handleSave = async () => {
    if (!employeeId) {
      toast.warning("Xodimni tanlang");
      return;
    }
    if (weekdays.length === 0) {
      toast.warning("Kamida bitta dam hafta kunini tanlang");
      return;
    }
    if (!reason.trim()) {
      toast.warning("Sababni kiriting");
      return;
    }

    const payload = {
      weekdays,
      reason: reason.trim(),
      is_paid: isPaid,
      is_active: isActive,
      effective_from: monthToDate(effectiveMonth),
    };

    if (editing?.id) {
      await updateMutation.mutateAsync({ id: editing.id, data: payload });
      return;
    }

    await createMutation.mutateAsync({
      employee_id: Number(employeeId),
      ...payload,
    });
  };

  const handleEdit = (row: any) => {
    setEditing(row);
    setEmployeeId(String(row.employee_id ?? row.employee?.id ?? ""));
    setWeekdays(Array.isArray(row.weekdays) ? row.weekdays : []);
    setReason(row.reason || "");
    setIsPaid(Boolean(row.is_paid));
    setIsActive(row.is_active !== false);
    setEffectiveMonth(dateToMonth(row.effective_from));
    setShowFormModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm("Dam kuni jadvalini o'chirishni tasdiqlaysizmi?")) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Shaxsiy dam kunlari</h1>
          <p className="text-slate-500 mt-1">Har xodim uchun hafta kunlari bo'yicha bir marta belgilanadi</p>
        </div>
        <Button onClick={openCreateModal}>Dam qo'shish</Button>
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 flex items-start gap-3 text-sm text-blue-800">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
        <div>
          Bu sahifada dam kunlari <strong>hafta nomlari</strong> bo'yicha saqlanadi. Har sanani alohida yozish shart emas.
        </div>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Xodim</label>
              <select
                className="w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm"
                value={filters.employee_id}
                onChange={(e) => {
                  setPage(1);
                  setFilters((p) => ({ ...p, employee_id: e.target.value }));
                }}
              >
                <option value="">Barcha xodimlar</option>
                {employees.map((emp: any) => (
                  <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Holat</label>
              <select
                className="w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm"
                value={filters.is_active}
                onChange={(e) => {
                  setPage(1);
                  setFilters((p) => ({ ...p, is_active: e.target.value }));
                }}
              >
                <option value="">Barchasi</option>
                <option value="true">Faol</option>
                <option value="false">Nofaol</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); resetForm(); }}
        title={editing ? "Dam kunini tahrirlash" : "Dam kunini qo'shish"}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Xodim *</label>
            <select
              className="w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              disabled={Boolean(editing)}
            >
              <option value="">Xodimni tanlang</option>
              {employees.map((emp: any) => (
                <option key={emp.id} value={emp.id}>{emp.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-600 block mb-2">Qaysi hafta kunlari dam? *</label>
            <div className="flex flex-wrap gap-2">
              {DAY_NAMES.map((d, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleWeekday(idx)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${weekdays.includes(idx)
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-600 border-slate-300"}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Boshlanish oyi (ixtiyoriy)</label>
            <Input type="month" value={effectiveMonth} onChange={(e) => setEffectiveMonth(e.target.value)} />
            <p className="text-xs text-slate-400 mt-1">Masalan: 2026-04 tanlansa, shu oydan boshlab hisoblanadi.</p>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Sabab *</label>
            <textarea
              className="w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Masalan: doimiy dam jadvali"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-4 h-4 accent-blue-600"
              checked={isPaid}
              onChange={(e) => setIsPaid(e.target.checked)}
            />
            <span className="text-sm text-slate-700">To'lanadigan dam kuni</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-4 h-4 accent-blue-600"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span className="text-sm text-slate-700">Faol</span>
          </label>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
              {editing ? "Saqlash" : "Saqlash"}
            </Button>
            <Button variant="outline" onClick={resetForm}>Tozalash</Button>
          </div>
        </div>
      </Modal>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Xodim</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Dam kunlari</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Boshlanish</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Sabab</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Holat</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Turi</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Amallar</th>
                </tr>
              </thead>
              <tbody>
                {patterns.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 px-4 text-center text-slate-500">Dam kuni jadvali topilmadi</td>
                  </tr>
                ) : (
                  patterns.map((row: any) => (
                    <tr key={row.id} className="border-b hover:bg-slate-50/70 transition">
                      <td className="py-3 px-4">{row.employee?.full_name || "-"}</td>
                      <td className="py-3 px-4">{weekdaysLabel(row.weekdays)}</td>
                      <td className="py-3 px-4">{row.effective_from ? String(row.effective_from).slice(0, 10) : "Darhol"}</td>
                      <td className="py-3 px-4">{row.reason || "-"}</td>
                      <td className="py-3 px-4">
                        <Badge variant={row.is_active ? "success" : "secondary"}>
                          {row.is_active ? "Faol" : "Nofaol"}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={row.is_paid ? "success" : "secondary"}>
                          {row.is_paid ? "To'lanadigan" : "To'lanmaydigan"}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(row)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(row.id)}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Pagination meta={meta} onPageChange={setPage} />

      {allPatterns.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-800">Dam kuni jadvali topilmadi</h3>
          <p className="text-slate-500">Yuqoridagi Dam qo'shish tugmasi orqali qo'shishingiz mumkin.</p>
        </div>
      )}
    </div>
  );
}
